import React, { useCallback, useContext } from 'react';
import { Layout, TextButton, Popover, Headline, IconButton } from '@oneaudi/unified-web-components';
import {
  AdditionalDealerDataActionsBottom,
  AdditionalDealerDataInfoButton,
  StyledText,
} from './AudiHeaderAdditionalDealerDataStyles';
import type {
  AudiHeaderDealerDataProps,
  Department,
} from '../../interfaces/header-components.interfaces';
import { DealerContext } from '../../context';
import useToggle from '../../hooks/use-toggle';
import { NAVIGATION_CLASSNAME } from '../../utils/globals';
import DealerDataServices from './DealerDataServices';
import styled from 'styled-components';
import { DealerDetailAlpha } from './DealerDetail_alpha';
import { DepartmentOpeningHoursAlpha } from './DepartmentOpeningHours_alpha';
import { messages } from '../../i18n/messages';
import { useI18n } from '@oneaudi/i18n-context';
import { ServiceContext } from '../../context/ServiceContext';
import { onDealerTrackingData } from '../../utils/tracking';

const StyledLayout = styled(Layout)`
  overflow-y: scroll;
  scrollbar-width: none;
  max-height: 65vh;
  @media (min-width: 768px) {
    max-width: 328px;
  }
`;

const StyledHeadline = styled(Headline)`
  -webkit-font-smoothing: antialiased;
`;

const StyledBackButton = styled(TextButton)`
  margin: ${({ theme }): string =>
    theme.type !== 'legacy'
      ? `${theme.spacing.l} 0 ${theme.spacing.xl} 0`
      : `${theme.spacing.l} 0 ${theme.spacing.l} 0`};
  ${({ theme }): string =>
    theme.type !== 'legacy' ? `font-size: ${theme.typography.fontSizeS}` : ''};
  ${({ theme }): string => (theme.type !== 'legacy' ? 'min-height: 36px' : '')};
  ${({ theme }): string => (theme.type !== 'legacy' ? `padding: ${theme.spacing.xs} 0` : '')};
`;

const AudiHeaderAdditionalDealerDataAlpha: React.FC<AudiHeaderDealerDataProps> = ({
  isMobileView,
  showDealerSearch,
}) => {
  const { dealerData, removeDealer } = useContext(DealerContext);
  const { trackingService } = useContext(ServiceContext);

  const backToDealerText = useI18n(messages.back_to_dealer);
  const vistDealerText = useI18n(messages.visit_dealer);
  const removeDealerButtonText = useI18n(messages.remove_dealer);
  const removeDealerHelpText = useI18n(messages.remove_dealer_help);
  const [toggleElement, toggle] = useToggle();
  const DealerButtonLabel = showDealerSearch ? vistDealerText : backToDealerText;

  const dealerHandleClick = useCallback(
    (event: React.MouseEvent, label: string): void => {
      trackingService?.track(onDealerTrackingData(event.target as Element, label));
    },
    [trackingService],
  );

  return (
    <StyledLayout
      className="dealer-container"
      padding={['var(--spacing-relative-lg)']}
      direction="column"
    >
      {!isMobileView && (
        <Layout className="services" direction="column">
          <StyledHeadline
            tag="h2"
            variant={{
              order: '2',
              type: 'headline',
              weight: 'normal',
            }}
            margin={[0, 0, 'var(--spacing-relative-sm)', 0]}
          >
            {dealerData?.additionalData?.displayName || dealerData?.name || 'Name not found'}
          </StyledHeadline>
          {dealerData && <DealerDataServices dealerData={dealerData} />}
        </Layout>
      )}

      <StyledBackButton
        id="dealerBack"
        variant="secondary"
        size="md"
        href={dealerData?.url || ''}
        onClick={(e: React.MouseEvent) => {
          dealerHandleClick(e, DealerButtonLabel);
        }}
        aria-label="link to dealer page"
        className={`back-to-dealer ${NAVIGATION_CLASSNAME}`}
      >
        {DealerButtonLabel}
      </StyledBackButton>

      {dealerData?.address && <DealerDetailAlpha icon="poi-icon" detail={dealerData?.address} />}
      {dealerData?.phone && <DealerDetailAlpha icon="voice-call-1" detail={dealerData?.phone} />}
      {dealerData?.openingHours &&
        dealerData?.openingHours.departments.map((department: Department) => (
          <DepartmentOpeningHoursAlpha key={department.departmentName} department={department} />
        ))}
      <AdditionalDealerDataActionsBottom isMobileView={isMobileView}>
        <TextButton
          aria-label={removeDealerButtonText}
          iconPosition="leading"
          icon="cancel"
          onClick={() => {
            removeDealer(removeDealerButtonText);
          }}
          size="sm"
          variant="tertiary"
          className={NAVIGATION_CLASSNAME}
        >
          {removeDealerButtonText}
        </TextButton>
        <AdditionalDealerDataInfoButton>
          <Popover
            aria-label="Popover content for remove dealer"
            placement="top"
            alignment="left"
            open={toggleElement}
            content={
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 0,
                  width: '100%',
                  maxWidth: 362,
                }}
              >
                <StyledText tag="p" variant={{ order: '1', style: 'normal', type: 'copy' }}>
                  {removeDealerHelpText}
                </StyledText>
              </div>
            }
            id="remove-dealer"
            triggerElement={
              <IconButton
                aria-label="triggers popover for remove dealer"
                icon="info-circle"
                aria-disabled={!open}
                id="remove-dealer-info"
                onClick={() => {
                  toggle();
                }}
                size="sm"
                variant="tertiary"
              />
            }
            onClose={() => {}}
            withTip={false}
          />
        </AdditionalDealerDataInfoButton>
      </AdditionalDealerDataActionsBottom>
    </StyledLayout>
  );
};

export default AudiHeaderAdditionalDealerDataAlpha;
